import React, { useContext, useState, useEffect, PropsWithChildren } from 'react';
import { Typography } from '../../../components/typography/typography';
import { TenantContext } from '../../../context/tenant-context';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Card } from '../../../components/card/card';
import { Divider } from '../../../components/divider/divider';
import Icon from '../../../components/icons/icon';
import { Toggle } from '../../../components/form/toggle';
import { EmailTemplate } from '../../../clients/quipli-service/email/email';
import { Tooltip } from '../../../components/tooltip/tooltip';
import './EmailTemplates.scss';
import { BreadCrumb, Breadcrumb } from '../../../components/breadcrumbs/breadcrumbs';
import { EmailTemplateModal } from './EmailTemplateModal';
import SkeletonLoader from '../../../components/skeleton-loader/skeleton-loader';
import { toast } from '../../../util/toasts';
import classNames from 'classnames';

export interface PlaceholderEmailTemplateResponse {
  category: string;
  name: string;
  label: string;
  description: string;
}

export const EmailTemplates = () => {
  const { getEmailTemplates, updateEmailTemplate } = useAPI();
  const { contextPath } = useContext(TenantContext);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
  const [categoriesToOpen, setCategoriesToOpen] = useState<Record<string, boolean>>({});
  const [initialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [partialLoading, setPartialLoading] = useState<boolean>(false);
  const [hideAdminAlert, setHideAdminAlert] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<'Customer Emails' | 'Admin Emails'>('Customer Emails');
  const navigate = useNavigate();

  const breadcrumbs: Breadcrumb[] = [{ label: 'Settings' }, { label: 'Email Customizations' }, { label: selectedTab }];

  const emailTemplatesByCategory = uniqueCategories.map((category) => {
    const templates = emailTemplates.filter(
      (template) => template.categoryName === category && selectedTab.includes(template.userType),
    );
    return { category, templates };
  });

  const fetchEmailTemplates = async () => {
    let response: EmailTemplate[] = [];
    try {
      response = await getEmailTemplates();
      setEmailTemplates(response);
    } catch (e) {
      toast('Failed to fetch email templates', 'error');
    }

    return response;
  };

  const openAllCategories = (uniqueCategories: string[]) => {
    const categoriesToOpen: Record<string, boolean> = {};
    uniqueCategories.forEach((category) => {
      categoriesToOpen[category] = true;
    });
    setCategoriesToOpen(categoriesToOpen);
  };

  useEffect(() => {
    openAllCategories(uniqueCategories);
  }, [selectedTab]);

  useEffect(() => {
    setIsInitialLoading(true);
    fetchEmailTemplates().then((response) => {
      const uniqueCategories = Array.from(new Set(response.map((item) => item.categoryName)));
      setUniqueCategories(uniqueCategories);
      openAllCategories(uniqueCategories);
      setIsInitialLoading(false);
    });
  }, []);

  const updateTemplateCall = async (template: EmailTemplate) => {
    try {
      await updateEmailTemplate(template);
      await fetchEmailTemplates();
    } catch (e) {
      toast('Failed to update email template', 'error');
    }
  };

  return (
    <div className="email-templates container-v2 p-0 sm:p-4 pb-5">
      <BreadCrumb breadcrumbs={breadcrumbs} />

      <EmailTemplateModal
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        selectedTemplate={selectedTemplate}
        isLoading={partialLoading}
        setIsLoading={setPartialLoading}
        update={async () => {
          if (!selectedTemplate) {
            return;
          }
          setPartialLoading(true);
          await updateTemplateCall({ ...selectedTemplate, enabled: !selectedTemplate.enabled });
          setPartialLoading(false);
          setIsModalOpen(false);
        }}
      />
      <div className="flex justify-between items-center pt-4">
        <Typography type="display-xs" weight="medium" className="text-[var(--grey-800)]">
          Email Customizations
        </Typography>
      </div>

      <div className="flex flex-row button-container w-full md:w-[30rem]">
        <div className={`slider ${selectedTab.replace(' ', '-').toLocaleLowerCase()}`} />

        <button className="button buttonv2" onClick={() => setSelectedTab('Customer Emails')} data-cy="customer-tab">
          <Typography type="text-md" weight="semi-bold" className="text-black">
            Customer Emails
          </Typography>
        </button>
        <button className="button buttonv2" onClick={() => setSelectedTab('Admin Emails')} data-cy="admin-tab">
          <Typography type="text-md" weight="semi-bold" className="text-black">
            Admin Emails
          </Typography>
        </button>
      </div>

      <Card className="email-templates-card">
        {initialLoading ? (
          <EditTemplateLoadingSkeleton />
        ) : (
          <>
            {selectedTab === 'Admin Emails' && (
              <div className={`admin-alert ${hideAdminAlert ? 'close' : 'open'}`}>
                <Typography type="text-md">
                  <strong>Important: </strong>Changes made here affect email notifications for{' '}
                  <strong>all admin users.</strong> Adjust invidiual user email settings in the{' '}
                  <a href={`${contextPath}manager/admins`} style={{ textDecoration: 'underline' }}>
                    Admin Users
                  </a>{' '}
                  section.
                </Typography>
                <div className="cursor-pointer" onClick={() => setHideAdminAlert(true)}>
                  <Icon icon="Close" />
                </div>
              </div>
            )}
            <EmailTemplateWrapper className="m-2 sm:ml-6 sm:mr-6">
              <Typography type="display-xs" weight="semi-bold">
                <p className="mb-2"> {selectedTab} Notifications</p>
              </Typography>
              <Typography type="text-md">
                <p className="mb-4">
                  Use the toggles below to enable or disable each email sent to all{' '}
                  {selectedTab === 'Admin Emails' ? 'admin' : 'customer'}s.
                  <ul>
                    <div className="hidden sm:block">
                      <li>
                        <strong>Hover over the information icon</strong> next to each email to see the conditions that
                        trigger it to send.
                      </li>
                    </div>
                    <li>
                      <strong>Click on any email to </strong>customize its subject line and message content.
                    </li>
                  </ul>
                </p>
              </Typography>
              <Divider />
              <EmailTemplateList>
                {initialLoading ? (
                  <div>Loading...</div>
                ) : (
                  emailTemplatesByCategory
                    .filter(({ templates }) => templates.length > 0)
                    .map(({ category, templates }) => (
                      <EmailTemplateCategory
                        key={`category-${category}`}
                        category={category}
                        isOpen={categoriesToOpen[category]}
                        onClick={() => setCategoriesToOpen((cto) => ({ ...cto, [category]: !cto[category] }))}
                      >
                        {templates.map((template) => (
                          <EmailTemplateItem
                            key={`template-${template.id}`}
                            template={template}
                            onClick={() => navigate(`${contextPath}manager/emailtemplatesv2/edit/${template.name}`)}
                          >
                            <Toggle
                              color="var(--quipli-yellow-300)"
                              onClick={async (e) => {
                                e.stopPropagation();

                                setSelectedTemplate(template);

                                if (template.enabled) {
                                  setIsModalOpen(true);
                                  return;
                                }
                                setPartialLoading(true);
                                await updateTemplateCall({ ...template, enabled: !template.enabled });
                                setPartialLoading(false);
                              }}
                              data-cy="email-template-toggle"
                              checked={template.enabled}
                            />
                            <div
                              className="cursor-pointer"
                              onClick={() => navigate(`${contextPath}manager/emailtemplatesv2/edit/${template.name}`)}
                            >
                              <Icon icon={'ForwardArrow'} />
                            </div>
                          </EmailTemplateItem>
                        ))}
                      </EmailTemplateCategory>
                    ))
                )}
              </EmailTemplateList>
            </EmailTemplateWrapper>
          </>
        )}
      </Card>
    </div>
  );
};

export const EmailTemplateWrapper = ({ className, children }: PropsWithChildren<{ className?: string }>) => {
  return <div className={classNames('email-templates-wrapper', className)}>{children}</div>;
};

export const EmailTemplateList = ({ children }: PropsWithChildren) => {
  return <div className="email-templates-list">{children}</div>;
};

export const EmailTemplateCategory = ({
  category,
  isOpen,
  onClick,
  children,
}: PropsWithChildren<{ category: string; isOpen: boolean; onClick: () => void }>) => {
  return (
    <div className="email-template-category">
      <div className="email-template-category-header cursor-pointer" onClick={onClick}>
        <Typography type="text-lg" weight="semi-bold">
          {category}
        </Typography>
        <div
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          data-cy="toggle-category"
        >
          <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} />
        </div>
      </div>
      <div className={`email-template-items ${isOpen ? 'open' : 'closed'}`}>{children}</div>
    </div>
  );
};

export const EmailTemplateItem = ({
  template,
  onClick,
  children,
}: PropsWithChildren<{ template: EmailTemplate; onClick: () => void }>) => {
  return (
    <div className="email-template-item cursor-pointer" onClick={onClick} data-cy="email-template-item">
      <div className="email-template-left">
        <div className="email-template-item-header">
          <Typography type="text-md" weight="semi-bold">
            {template.displayTitle}
          </Typography>
          <div className="hidden sm:block ml-1 pt-1">
            <Tooltip type="black" tooltip={template.displayTooltip} maxWidth="18rem">
              <Icon icon={'HelpCircle'} />
            </Tooltip>
          </div>
        </div>
        <div className="email-template-item-body">
          <Typography type="text-sm" weight="regular">
            {template.displayDescription}
          </Typography>
        </div>
      </div>
      {children !== undefined && <div className="email-template-right gap-4">{children}</div>}
    </div>
  );
};

export const EditTemplateLoadingSkeleton = () => (
  <div className="edit-email-template-loading-skeleton m-2 sm:ml-6 sm:mr-6">
    <div className="header">
      <SkeletonLoader style={{ width: '100%', height: '11rem' }} />
    </div>
    <div className="content mt-4">
      <SkeletonLoader style={{ width: '100%', height: '11rem' }} />
    </div>
  </div>
);
